/** O que a folha "Mais" do celular oferece, e para quem.
 *
 * Fora do `App` porque é **decisão**, e não desenho: quais destinos existem
 * depende das permissões de quem olha, e essa é a parte que erra. Dentro do
 * componente, testá-la exigiria montar a aplicação inteira e abrir a folha para
 * cada permissão — e o `App` já é o arquivo menos coberto do repositório.
 *
 * Os portões são os mesmos da barra superior, de propósito e no mesmo commit:
 * duas navegações que decidissem por conta própria discordariam sobre o que
 * existe na primeira permissão nova.
 */

import type { ItemDoMenu } from "@/shared/ui/MaisDoCelular";

export interface AcoesDoMais {
  aoAbrirIndicadores: () => void;
  aoAbrirPessoas: () => void;
  aoConvidar: () => void;
  aoAbrirEtiquetas: () => void;
  aoAbrirCanal: () => void;
  aoAbrirAuditoria: () => void;
  aoAbrirConta: () => void;
  aoPedirAjuda: () => void;
  aoSair: () => void;
}

export function itensDoMais(
  temPermissao: (chave: string) => boolean,
  acoes: AcoesDoMais,
): ItemDoMenu[] {
  return [
    // Sem portão: o painel só soma o que o funil já mostra, e quem entra no
    // funil vê o funil inteiro (SPEC-ANL-001 § 5).
    {
      rotulo: "Indicadores",
      descricao: "O mês em números — pipeline, ganho e conversão.",
      aoAcionar: acoes.aoAbrirIndicadores,
    },
    ...(temPermissao("user:manage")
      ? [
          {
            rotulo: "Pessoas",
            descricao: "Quem tem acesso, e com qual papel.",
            aoAcionar: acoes.aoAbrirPessoas,
          },
        ]
      : []),
    ...(temPermissao("user:invite")
      ? [
          {
            rotulo: "Convidar pessoa",
            descricao: "Chamar alguém para a conta da empresa.",
            aoAcionar: acoes.aoConvidar,
          },
        ]
      : []),
    ...(temPermissao("opportunity:manage")
      ? [
          {
            rotulo: "Etiquetas",
            descricao: "O vocabulário que marca os cards do funil.",
            aoAcionar: acoes.aoAbrirEtiquetas,
          },
        ]
      : []),
    ...(temPermissao("whatsapp:manage")
      ? [
          {
            rotulo: "WhatsApp",
            descricao: "Para onde vai a conversa da empresa.",
            aoAcionar: acoes.aoAbrirCanal,
          },
        ]
      : []),
    // `audit:export` e não `audit:read`: quem só lê a trilha na tela não tem
    // como exportá-la, e um item que leva a `403` ensina que o produto quebra.
    ...(temPermissao("audit:export")
      ? [
          {
            rotulo: "Auditoria",
            descricao: "A trilha do que aconteceu, para exportar.",
            aoAcionar: acoes.aoAbrirAuditoria,
          },
        ]
      : []),
    {
      rotulo: "Minha conta",
      descricao: "Seus dados, senha e segundo fator.",
      aoAcionar: acoes.aoAbrirConta,
    },
    {
      rotulo: "Ajuda desta tela",
      descricao: "O guia da tela que estava aberta.",
      aoAcionar: acoes.aoPedirAjuda,
    },
    // A saída fica por último, e depois da ajuda: quem procura ajuda não pode
    // encontrar a saída primeiro — a mesma ordem da barra superior.
    { rotulo: "Sair", descricao: "Encerrar a sessão neste aparelho.", aoAcionar: acoes.aoSair },
  ];
}
